const YOUTUBE_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be']

/**
 * Returns an error message if the given string doesn't look like a valid http(s) URL.
 * Returns null if the string is empty (let required-field logic handle empties) or valid.
 */
export function getUrlLikenessError(url: string): string | null {
  const trimmed = url.trim()
  if (!trimmed) return null
  // `new URL()` silently encodes/strips internal whitespace, so reject up front.
  if (/\s/.test(trimmed)) {
    return 'URL cannot contain spaces or line breaks'
  }
  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'URL must start with http:// or https://'
    }
    // Rejects localhost, IPv6 literals, and bare hostnames like `https://foo` that
    // the backend cannot fetch from. IPv4 addresses naturally have dots and pass.
    if (!parsed.hostname.includes('.')) {
      return 'URL must point to a public domain (e.g. https://example.com)'
    }
    return null
  } catch {
    return "That doesn't look like a valid URL. Make sure it starts with https:// (e.g. https://example.com)"
  }
}

/**
 * Returns an error message if the URL is a YouTube playlist, channel, or other non-video page.
 * Returns null if the URL is fine (either not YouTube, or a valid video URL).
 */
export function getYouTubeUrlError(url: string): string | null {
  try {
    const parsed = new URL(url)
    const host = parsed.hostname.toLowerCase()

    if (!YOUTUBE_HOSTS.includes(host)) return null

    if (parsed.pathname === '/playlist') {
      return 'YouTube playlist URLs are not supported. Please provide a direct video URL (e.g. youtube.com/watch?v=...).'
    }

    if (
      parsed.pathname.startsWith('/@') ||
      parsed.pathname.startsWith('/channel/') ||
      parsed.pathname.startsWith('/c/')
    ) {
      return 'YouTube channel URLs are not supported. Please provide a direct video URL.'
    }

    if (parsed.pathname === '/watch' && parsed.searchParams.has('list') && !parsed.searchParams.has('v')) {
      return 'This appears to be a playlist URL. Please provide a direct video URL instead.'
    }

    return null
  } catch {
    return null
  }
}

const YOUTUBE_VIDEO_HOSTS = new Set([
  'youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'gaming.youtube.com',
  'youtube-nocookie.com',
])
const YOUTUBE_SHORT_HOSTS = new Set(['youtu.be'])
const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/

function normalizeHost(hostname: string): string {
  const host = hostname.toLowerCase()
  return host.startsWith('www.') ? host.slice(4) : host
}

/**
 * Extracts the 11-char video ID from any supported YouTube URL form
 * (watch, youtu.be, shorts, embed, v, live). Returns null if the URL isn't a
 * recognizable YouTube video. Mirrors the backend `YouTubeIdDetector`.
 */
export function getYouTubeVideoId(url: string): string | null {
  let parsed: URL
  try {
    parsed = new URL(url.trim())
  } catch {
    return null
  }

  const host = normalizeHost(parsed.hostname)

  if (YOUTUBE_SHORT_HOSTS.has(host)) {
    const id = parsed.pathname.split('/')[1] ?? ''
    return YOUTUBE_ID_RE.test(id) ? id : null
  }

  if (!YOUTUBE_VIDEO_HOSTS.has(host)) return null

  const path = parsed.pathname.replace(/\/+$/, '')

  if (path === '/watch') {
    const id = parsed.searchParams.get('v') ?? ''
    return YOUTUBE_ID_RE.test(id) ? id : null
  }

  if (['/shorts/', '/embed/', '/v/', '/live/'].some(prefix => path.startsWith(prefix))) {
    const id = path.split('/')[2] ?? ''
    return YOUTUBE_ID_RE.test(id) ? id : null
  }

  return null
}

export type YouTubeEmbeddability =
  { status: 'embeddable' } | { status: 'not-embeddable'; reason: 'unavailable' | 'not-found' } | { status: 'unknown' }

const OEMBED_TIMEOUT_MS = 2500

/**
 * Best-effort embeddability check via YouTube's CORS-enabled oEmbed endpoint.
 * 200 = embeddable, 401 = embedding disabled or private, 404 = deleted/bad ID.
 * Anything else (403/429/5xx/network/timeout) resolves to 'unknown' so the
 * caller can fail open and never block a legitimate add.
 */
export async function checkYouTubeEmbeddable(url: string): Promise<YouTubeEmbeddability> {
  const videoId = getYouTubeVideoId(url)
  if (!videoId) return { status: 'unknown' }

  // oEmbed's status depends on the URL form (a raw /embed/ URL 404s for every
  // video), so always query the canonical watch URL built from the ID.
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}`
  const endpoint = `https://www.youtube.com/oembed?url=${encodeURIComponent(watchUrl)}&format=json`

  try {
    const response = await fetch(endpoint, { signal: AbortSignal.timeout(OEMBED_TIMEOUT_MS) })

    if (response.ok) return { status: 'embeddable' }
    if (response.status === 401) return { status: 'not-embeddable', reason: 'unavailable' }
    if (response.status === 404) return { status: 'not-embeddable', reason: 'not-found' }

    return { status: 'unknown' }
  } catch {
    return { status: 'unknown' }
  }
}
